import * as Sentry from '@sentry/react';
import { db } from '../db';
import type {
  CachedOperatorCombo,
  OperatorComboStart,
  OperatorComboCompletion,
} from '../db';
import { operatorSpraySessionService } from './operatorSpraySessionService';
import type { MyOperatorSessionRow } from './operatorSpraySessionService';
import type { IntensityLabel } from './sprayService';

/**
 * Operator combo local cache for the per-combo Field Mode flow.
 *
 * Reads and writes to the Dexie operatorCombos / operatorComboStarts /
 * operatorComboCompletes tables (introduced in v11 by Phase 6 of the
 * multi-operator spray sessions track). Mirrors the role
 * spraySessionCacheService plays for the legacy session-level operator
 * list: it's the single read-side surface for the combo flow and owns
 * the optimistic writes the UI dispatches on Start/Complete/End-early/
 * Abandon.
 *
 * The merge invariant: a combo row with at least one pending entry in
 * operatorComboStarts OR operatorComboCompletes is the operator's local
 * truth and must NOT be overwritten by a refresh — the queue drainer
 * (Task 6.10) still has to replay those submissions before the server's
 * view becomes authoritative again.
 *
 * Primitives:
 *   refreshMyOperatorCombos   — pull /operator-sessions/my, merge with
 *                               cache, preserving pending-queued rows.
 *   readCachedOperatorCombos  — for the operator combo list; returns
 *                               rows sorted by parentDate desc.
 *   getOperatorComboWithFallback — single-combo load with offline-aware
 *                                  fallback (network errors only).
 *   applyLocalStartedCombo    — optimistically flip a cached row to
 *                               in_progress AND append a Start queue
 *                               entry.
 *   applyLocalTerminalCombo   — optimistically flip a cached row to its
 *                               terminal state AND append a Completion
 *                               queue entry (action ∈ {complete,
 *                               end-early, abandon}).
 *   purgeTerminalCombo        — drop a cached row after a successful
 *                               terminal sync.
 *   getPendingQueueEntries    — surface a combo's queued Starts +
 *                               Completions for the UI's read-only
 *                               render of an unsynced submission.
 */

const NON_SYNCED_STATUSES: ReadonlyArray<OperatorComboStart['syncStatus']> = [
  'pending',
  'syncing',
  'failed',
  'quarantined',
];

// Pin only by IN-FLIGHT entries when reconciling refresh-vs-cache. A
// `quarantined` queue entry is the server's deliberate rejection of a
// submission — retrying will not help. If the server then stops
// returning the combo (e.g. its parent session was soft-deleted), we
// must let the next refresh purge the stale local row instead of
// keeping it pinned forever. The pin existed to protect work the
// drainer hadn't yet replayed; that no longer applies once an entry is
// quarantined.
const IN_FLIGHT_QUEUE_STATUSES: ReadonlyArray<OperatorComboStart['syncStatus']> = [
  'pending',
  'syncing',
  'failed',
];

/**
 * Map a wire MyOperatorSessionRow into the denormalised cache shape.
 *
 * EXPORTED for Task 6.10 — the queue drainer re-fetches server truth
 * after a successful sync and needs to rewrite the cache with the same
 * shape this module produces.
 *
 * The wire response carries `tenantId` on the document even though the
 * OperatorSpraySessionCombo TypeScript type omits it; we read it
 * defensively so the cached row is self-contained.
 */
export function toCachedShape(
  wire: MyOperatorSessionRow,
  cachedAt: string,
): CachedOperatorCombo {
  const rawProgramId = wire.sessionId.programId;
  const programId =
    typeof rawProgramId === 'object' && rawProgramId !== null
      ? { _id: String((rawProgramId as { _id: string })._id), name: (rawProgramId as { name?: string }).name ?? '' }
      : { _id: String(rawProgramId), name: '' };

  return {
    _id: wire._id,
    tenantId: (wire as unknown as { tenantId?: string }).tenantId ?? '',
    parentId: wire.sessionId._id,
    parentDate: wire.sessionId.date,
    sessionId: {
      _id: wire.sessionId._id,
      date: wire.sessionId.date,
      orchardIds: wire.sessionId.orchardIds,
      programId,
      roundId: wire.sessionId.roundId,
      status: wire.sessionId.status ?? 'planned',
      ...((wire.sessionId as { name?: string }).name !== undefined
        ? { name: (wire.sessionId as { name?: string }).name }
        : {}),
      ...((wire.sessionId as { seq?: string | number }).seq !== undefined
        ? { seq: (wire.sessionId as { seq?: string | number }).seq }
        : {}),
      ...((wire.sessionId as { intensityLevel?: IntensityLabel }).intensityLevel !== undefined
        ? { intensityLevel: (wire.sessionId as { intensityLevel?: IntensityLabel }).intensityLevel }
        : {}),
    },
    operatorId: wire.operatorId,
    machineId: wire.machineId,
    // Phase 6b — per-combo intensity. The wire payload may not yet carry
    // this field for legacy combos created before Phase 6b shipped; cache
    // it when present so the offline resolver falls back to the parent.
    ...((wire as { intensityLevel?: IntensityLabel }).intensityLevel !== undefined
      ? { intensityLevel: (wire as { intensityLevel?: IntensityLabel }).intensityLevel }
      : {}),
    status: wire.status,
    startedAt: wire.startedAt,
    endedAt: wire.endedAt,
    ppeChecklist: (wire.ppeChecklist ?? []).map(item => ({
      item: item.item,
      checked: item.checked,
      checkedAt: item.checkedAt ?? null,
    })),
    // The wire ComboWeatherSnapshot type is the legacy numeric shape
    // (temperature/windSpeed/precipitation); the cached shape is the
    // newer categorical envelope. Phase 6 will reconcile these — for now
    // pass the field through opaquely so a wire payload that already
    // carries the new categorical fields survives the round-trip.
    weatherSnapshots: wire.weatherSnapshots as unknown as CachedOperatorCombo['weatherSnapshots'],
    cachedAt,
  };
}

/**
 * Collect the set of combo IDs that have at least one IN-FLIGHT
 * (pending / syncing / failed) entry in either offline queue. These
 * combos are locally-owned and must not be deleted by refresh — the
 * drainer still owes a sync attempt.
 *
 * Quarantined entries are deliberately excluded so refresh can purge
 * stale cached combos once the server stops returning them.
 */
async function inFlightQueueComboIds(): Promise<Set<string>> {
  const [starts, completes] = await Promise.all([
    db.operatorComboStarts
      .where('syncStatus')
      .anyOf(IN_FLIGHT_QUEUE_STATUSES as unknown as string[])
      .toArray(),
    db.operatorComboCompletes
      .where('syncStatus')
      .anyOf(IN_FLIGHT_QUEUE_STATUSES as unknown as string[])
      .toArray(),
  ]);
  const ids = new Set<string>();
  for (const e of starts) ids.add(e.comboId);
  for (const e of completes) ids.add(e.comboId);
  return ids;
}

/**
 * Pull /operator-sessions/my and reconcile the local cache:
 *   - Server rows whose _id is locally in-flight → leave the cached row as-is.
 *   - Server rows otherwise → upsert in place.
 *   - Cached rows the server omits → delete, UNLESS locally in-flight.
 *     Quarantined queue entries do NOT pin (see IN_FLIGHT_QUEUE_STATUSES).
 *     When a combo is purged, any orphan quarantined queue rows for it
 *     are cleaned up too so they don't survive as ghosts attached to
 *     a comboId the user can no longer reach.
 */
export async function refreshMyOperatorCombos(): Promise<{ hasLinkedOperator: boolean }> {
  return Sentry.startSpan(
    { name: 'spray.cache.refreshMyOperatorCombos', op: 'cache.sync' },
    async () => {
      try {
        const { sessions, hasLinkedOperator } = await operatorSpraySessionService.getMyOperatorSessions();
        const inFlightIds = await inFlightQueueComboIds();
        const now = new Date().toISOString();

        const existing = await db.operatorCombos.toArray();
        const serverIds = new Set<string>();
        const toUpsert: CachedOperatorCombo[] = [];
        for (const wire of sessions) {
          serverIds.add(wire._id);
          if (inFlightIds.has(wire._id)) continue;
          toUpsert.push(toCachedShape(wire, now));
        }
        if (toUpsert.length) await db.operatorCombos.bulkPut(toUpsert);

        const toDelete: string[] = [];
        for (const row of existing) {
          if (serverIds.has(row._id)) continue;
          if (inFlightIds.has(row._id)) continue;
          toDelete.push(row._id);
        }
        if (toDelete.length) {
          await db.operatorCombos.bulkDelete(toDelete);
          // Purge orphan quarantined queue rows for the deleted combos.
          // Quarantined-only combos lost their pin (see
          // IN_FLIGHT_QUEUE_STATUSES); without this cleanup the rows
          // would linger forever in IndexedDB.
          for (const comboId of toDelete) {
            await db.operatorComboStarts
              .where('comboId')
              .equals(comboId)
              .delete();
            await db.operatorComboCompletes
              .where('comboId')
              .equals(comboId)
              .delete();
          }
        }

        return { hasLinkedOperator };
      } catch (err) {
        Sentry.captureException(err);
        throw err;
      }
    },
  );
}

/**
 * Return every cached operator combo, sorted by parentDate descending
 * (newest spray date first) so the operator's "today / upcoming" combos
 * surface at the top of the list.
 */
export async function readCachedOperatorCombos(): Promise<CachedOperatorCombo[]> {
  const rows = await db.operatorCombos.toArray();
  rows.sort((a, b) => (a.parentDate < b.parentDate ? 1 : a.parentDate > b.parentDate ? -1 : 0));
  return rows;
}

/**
 * Single-combo load with offline-aware fallback. Online callers get
 * the freshest /operator-sessions/:id payload; offline callers — or
 * those who hit a transient network error — fall back to the local
 * cache so the operator can still open a cached row and act on it.
 *
 * As of #300 the single-combo route deep-populates the parent session, so
 * the normal path maps the wire through `toCachedShape` (the same mapper
 * the bulk /operator-sessions/my refresh uses) for full parent parity. A
 * defensive branch still handles a raw-ObjectId sessionId (legacy /
 * unexpected responses) by borrowing the cached parent context or writing
 * empty placeholders.
 */
export async function getOperatorComboWithFallback(
  comboId: string,
): Promise<CachedOperatorCombo | null> {
  try {
    const wire = await operatorSpraySessionService.getCombo(comboId);
    const now = new Date().toISOString();

    // Normal path (post-#300): the single-combo route deep-populates the
    // parent session, so the wire matches the bulk MyOperatorSessionRow
    // shape. Reuse the same mapper the /my refresh uses for full field
    // parity (parentDate, name, seq, roundId, intensityLevel) — one source
    // of truth, no hand-rolled placeholder drift.
    const sessionRef: unknown = wire.sessionId;
    if (typeof sessionRef === 'object' && sessionRef !== null) {
      const cached = toCachedShape(wire, now);
      await db.operatorCombos.put(cached);
      return cached;
    }

    // Defensive legacy path: a raw-ObjectId sessionId (a pre-#300 or
    // otherwise unexpected response). Borrow the denormalised parent
    // context from the most recent cached row when present; if none
    // exists, write empty placeholders (the UI is expected to also fetch
    // the parent). Kept as a catch-all so the cache never crashes on a
    // wire shape that violates the populated contract.
    const existing = await db.operatorCombos.get(comboId);
    const cached: CachedOperatorCombo = {
      _id: wire._id,
      tenantId:
        existing?.tenantId ?? (wire as unknown as { tenantId?: string }).tenantId ?? '',
      parentId: existing?.parentId ?? String(sessionRef),
      parentDate: existing?.parentDate ?? '',
      sessionId: existing?.sessionId ?? {
        _id: String(sessionRef),
        date: '',
        orchardIds: [],
        programId: { _id: '', name: '' },
        status: 'planned',
      },
      operatorId: wire.operatorId,
      machineId: wire.machineId,
      ...((wire as { intensityLevel?: IntensityLabel }).intensityLevel !== undefined
        ? { intensityLevel: (wire as { intensityLevel?: IntensityLabel }).intensityLevel }
        : existing?.intensityLevel !== undefined
          ? { intensityLevel: existing.intensityLevel }
          : {}),
      status: wire.status,
      startedAt: wire.startedAt,
      endedAt: wire.endedAt,
      ppeChecklist: (wire.ppeChecklist ?? []).map(item => ({
        item: item.item,
        checked: item.checked,
        checkedAt: item.checkedAt ?? null,
      })),
      weatherSnapshots:
        wire.weatherSnapshots as unknown as CachedOperatorCombo['weatherSnapshots'],
      cachedAt: now,
    };
    await db.operatorCombos.put(cached);
    return cached;
  } catch (err: unknown) {
    // Only fall back when this looks like a network-layer failure. A
    // backend 4xx/5xx is a real signal — e.g., 404 means the combo was
    // deleted, and silently substituting the cache would mask
    // legitimate state divergence.
    const e = err as { response?: unknown; cause?: { response?: unknown } };
    const hasBackendResponse = !!(e?.response || e?.cause?.response);
    if (hasBackendResponse) throw err;
    const cached = await db.operatorCombos.get(comboId);
    if (cached) return cached;
    return null;
  }
}

/**
 * Optimistically flip a cached combo to in_progress. Called by the
 * Start workflow immediately after queueing — keeps the UI responsive
 * while the network call is in flight or the queue waits to drain.
 *
 * Best-effort cache write: if no cached row exists (e.g., the operator
 * opened the combo via deep-link before the first refresh landed), we
 * still append the queue entry so the drainer can replay it.
 */
export async function applyLocalStartedCombo(
  comboId: string,
  payload: OperatorComboStart['payload'],
): Promise<void> {
  const now = new Date().toISOString();
  const startedAt = payload.startedAt ?? now;
  const row = await db.operatorCombos.get(comboId);
  if (row) {
    const checkedItems = new Set((payload.ppeChecks ?? []).filter(p => p.checked).map(p => p.item));
    const nextPpeChecklist = row.ppeChecklist.map(item =>
      checkedItems.has(item.item)
        ? { ...item, checked: true, checkedAt: now }
        : item,
    );
    await db.operatorCombos.put({
      ...row,
      status: 'in_progress',
      startedAt,
      ppeChecklist: nextPpeChecklist,
      cachedAt: now,
    });
  }
  await db.operatorComboStarts.add({
    comboId,
    payload,
    syncStatus: 'pending',
    createdAt: now,
  });
}

/**
 * Optimistically flip a cached combo to its terminal state and append a
 * Completion queue entry. The `action` discriminator maps to the
 * terminal status:
 *   'complete'  → 'completed'
 *   'end-early' → 'incomplete'
 *   'abandon'   → 'cancelled'
 *
 * `endedAt` is stamped only on complete / end-early; abandon has no
 * endTime in its payload because the combo never reached an end state
 * worth recording.
 */
export async function applyLocalTerminalCombo(
  comboId: string,
  action: 'complete' | 'end-early' | 'abandon',
  payload: OperatorComboCompletion['payload'],
): Promise<void> {
  const now = new Date().toISOString();
  const row = await db.operatorCombos.get(comboId);
  if (row) {
    const nextStatus: CachedOperatorCombo['status'] =
      action === 'complete' ? 'completed' : action === 'end-early' ? 'incomplete' : 'cancelled';
    const nextEndedAt =
      action === 'abandon' ? row.endedAt : (payload.endTime ?? now);
    await db.operatorCombos.put({
      ...row,
      status: nextStatus,
      endedAt: nextEndedAt,
      cachedAt: now,
    });
  }
  await db.operatorComboCompletes.add({
    comboId,
    action,
    payload,
    syncStatus: 'pending',
    createdAt: now,
  });
}

/**
 * Drop a cached row after the terminal /complete | /end-early |
 * /abandon sync succeeds. The terminal combo never returns from
 * /operator-sessions/my again (the route filters to non-terminal
 * statuses), so removing it locally keeps the cache aligned with the
 * server. Queue entries are NOT touched here — those are owned by the
 * drainer, which deletes them on successful sync.
 */
export async function purgeTerminalCombo(comboId: string): Promise<void> {
  await db.operatorCombos.delete(comboId);
}

/**
 * Return the pending Start + Completion queue entries for one combo.
 * Drives the UI's read-only render of an unsynced submission — the
 * combo workflow page surfaces "Queued (pending sync)" when either
 * list is non-empty.
 */
export async function getPendingQueueEntries(
  comboId: string,
): Promise<{ starts: OperatorComboStart[]; completes: OperatorComboCompletion[] }> {
  const [starts, completes] = await Promise.all([
    db.operatorComboStarts
      .where('comboId')
      .equals(comboId)
      .filter(r => (NON_SYNCED_STATUSES as ReadonlyArray<string>).includes(r.syncStatus))
      .toArray(),
    db.operatorComboCompletes
      .where('comboId')
      .equals(comboId)
      .filter(r => (NON_SYNCED_STATUSES as ReadonlyArray<string>).includes(r.syncStatus))
      .toArray(),
  ]);
  return { starts, completes };
}
